import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { useTranslation } from '../redux/TranslationHelper';
import './CompanyHistoryComponent.css';

const CompanyHistoryComponent = (props) => {
  const { t } = useTranslation();
  
  const defaultItems = [
    {
      id: uuidv4(),
      year: '2010',
      title: t('회사 설립'),
      description: t('혁신적인 비전을 가지고 회사가 설립되었습니다.'),
      image: ''
    },
    {
      id: uuidv4(),
      year: '2012',
      title: t('첫 제품 출시'),
      description: t('시장에서 좋은 반응을 얻은 첫 제품을 출시했습니다.'),
      image: ''
    },
    {
      id: uuidv4(),
      year: '2015',
      title: t('해외 시장 진출'),
      description: t('글로벌 시장으로 확장하여 아시아 지역에 첫 해외 지사를 설립했습니다.'),
      image: ''
    },
    {
      id: uuidv4(),
      year: '2018',
      title: t('주요 투자 유치'),
      description: t('대규모 투자 유치에 성공하여 사업 확장의 기반을 마련했습니다.'),
      image: ''
    },
    {
      id: uuidv4(),
      year: '2020',
      title: t('기술 혁신상 수상'),
      description: t('혁신적인 기술력을 인정받아 국내 기술 혁신상을 수상했습니다.'),
      image: ''
    },
    {
      id: uuidv4(),
      year: '2023',
      title: t('신규 서비스 론칭'),
      description: t('고객 중심의 혁신적인 서비스를 성공적으로 출시했습니다.'),
      image: ''
    }
  ];

  // state 정의 및 props에서 초기값 설정
  const [historyItems, setHistoryItems] = useState(props.historyItems || defaultItems);
  const [timelineStyle, setTimelineStyle] = useState(props.timelineStyle || 'vertical');
  const [colorScheme, setColorScheme] = useState(props.colorScheme || {
    primary: '#3498db',
    secondary: '#f1c40f',
    background: '#ffffff',
    text: '#333333'
  });
  const [animation, setAnimation] = useState(props.animation !== undefined ? props.animation : true);
  const [imageWidth, setImageWidth] = useState(props.imageWidth || props.imageSize || 150);
  const [imageHeight, setImageHeight] = useState(props.imageHeight || props.imageSize || 150);

  // props가 변경될 때 state 업데이트
  useEffect(() => {
    if (props.historyItems) {
      setHistoryItems(props.historyItems);
    }
  }, [props.historyItems]);

  useEffect(() => {
    if (props.timelineStyle) {
      setTimelineStyle(props.timelineStyle);
    }
  }, [props.timelineStyle]);

  useEffect(() => {
    if (props.colorScheme) {
      setColorScheme(props.colorScheme);
    }
  }, [props.colorScheme]);

  useEffect(() => {
    if (props.animation !== undefined) {
      setAnimation(props.animation);
    }
  }, [props.animation]);

  useEffect(() => {
    if (props.imageWidth) {
      setImageWidth(props.imageWidth);
    } else if (props.imageSize) {
      setImageWidth(props.imageSize);
    }
  }, [props.imageWidth, props.imageSize]);

  useEffect(() => {
    if (props.imageHeight) {
      setImageHeight(props.imageHeight);
    } else if (props.imageSize) {
      setImageHeight(props.imageSize);
    }
  }, [props.imageHeight, props.imageSize]);

  // 에디터에서 편집 가능하도록 속성 변경 함수들
  const updateTimelineStyle = (newStyle) => {
    setTimelineStyle(newStyle);
  };

  const updateColorScheme = (newColorScheme) => {
    setColorScheme({ ...colorScheme, ...newColorScheme });
  };

  const toggleAnimation = () => {
    setAnimation(!animation);
  };

  // 타임라인 항목 추가
  const addHistoryItem = (item) => {
    setHistoryItems([...historyItems, { ...item, id: uuidv4() }]);
  };

  // 타임라인 항목 업데이트
  const updateHistoryItem = (id, updatedItem) => {
    const updated = historyItems.map(item =>
      item.id === id ? { ...item, ...updatedItem } : item
    );
    setHistoryItems(updated);
  };

  // 타임라인 항목 삭제
  const deleteHistoryItem = (id) => {
    setHistoryItems(historyItems.filter(item => item.id !== id));
  };

  // 수직형 타임라인 렌더링
  const renderVerticalTimeline = () => (
    <div className="company-history-timeline vertical">
      {historyItems.map((item, index) => {
        const isEven = index % 2 === 0;
        
        return (
          <div 
            key={item.id} 
            className={`timeline-item ${animation ? 'animate' : ''} ${isEven ? 'even' : 'odd'}`}
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            {/* 왼쪽 영역: 짝수면 텍스트, 홀수면 이미지 */}
            <div className="timeline-left">
              {isEven ? (
                <div 
                  className="timeline-content text-right"
                  style={{ backgroundColor: colorScheme.background, borderColor: colorScheme.primary }}
                >
                  <h3 style={{ color: colorScheme.primary }}>{typeof item.title === 'string' ? t(item.title) : item.title}</h3>
                  <p style={{ color: colorScheme.text }}>{typeof item.description === 'string' ? t(item.description) : item.description}</p>
                </div>
              ) : (
                item.image && (
                  <div className="timeline-image" style={{ 
                    width: `${imageWidth}px`, 
                    height: `${imageHeight}px`,
                    padding: '5px',
                    backgroundColor: colorScheme.background,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img src={item.image} alt={typeof item.title === 'string' ? t(item.title) : item.title} />
                  </div>
                )
              )}
            </div>
            
            {/* 중앙 연도 */}
            <div className="timeline-center">
              <div 
                className="timeline-year" 
                style={{ 
                  backgroundColor: colorScheme.primary, 
                  color: colorScheme.background,
                  position: 'relative',
                  zIndex: 4
                }}
              >
                {item.year}
              </div>
            </div>
            
            {/* 오른쪽 영역: 짝수면 이미지, 홀수면 텍스트 */}
            <div className="timeline-right">
              {isEven ? (
                item.image && (
                  <div className="timeline-image" style={{ 
                    width: `${imageWidth}px`, 
                    height: `${imageHeight}px`,
                    padding: '5px',
                    backgroundColor: colorScheme.background,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img src={item.image} alt={typeof item.title === 'string' ? t(item.title) : item.title} />
                  </div>
                )
              ) : (
                <div 
                  className="timeline-content text-left"
                  style={{ backgroundColor: colorScheme.background, borderColor: colorScheme.primary }}
                >
                  <h3 style={{ color: colorScheme.primary }}>{typeof item.title === 'string' ? t(item.title) : item.title}</h3>
                  <p style={{ color: colorScheme.text }}>{typeof item.description === 'string' ? t(item.description) : item.description}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // 수평형 타임라인 렌더링
  const renderHorizontalTimeline = () => (
    <div className="company-history-timeline horizontal">
      <div className="timeline-line" style={{ backgroundColor: colorScheme.primary }}></div>
      <div className="timeline-items-container">
        {historyItems.map((item, index) => (
          <div 
            key={item.id || index} 
            className={`timeline-item ${animation ? 'animate' : ''}`}
            style={{ 
              animationDelay: `${index * 0.2}s`,
              left: `${(index / (historyItems.length - 1)) * 100}%`
            }}
          >
            <div 
              className="timeline-point" 
              style={{ backgroundColor: colorScheme.secondary }}
            ></div>
            <div 
              className="timeline-year" 
              style={{ color: colorScheme.primary }}
            >
              {item.year}
            </div>
            
            {item.image && (
              <div className="timeline-image horizontal-image" style={{ 
                width: `${imageWidth}px`, 
                height: `${imageHeight}px`,
                padding: '5px',
                backgroundColor: colorScheme.background,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img src={item.image} alt={typeof item.title === 'string' ? t(item.title) : item.title} />
              </div>
            )}
            
            <div 
              className="timeline-content"
              style={{ backgroundColor: colorScheme.background, borderColor: colorScheme.primary }}
            >
              <h3 style={{ color: colorScheme.primary }}>{typeof item.title === 'string' ? t(item.title) : item.title}</h3>
              <p style={{ color: colorScheme.text }}>{typeof item.description === 'string' ? t(item.description) : item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="company-history-component">
      <div className="history-header">
        <h2>{props.title ? (typeof props.title === 'string' ? t(props.title) : props.title) : t('회사 연혁')}</h2>
        {props.subtitle && <p className="subtitle">{typeof props.subtitle === 'string' ? t(props.subtitle) : props.subtitle}</p>}
      </div>
      
      {timelineStyle === 'vertical' ? renderVerticalTimeline() : renderHorizontalTimeline()}
    </div>
  );
};

export default CompanyHistoryComponent; 